/**
 * Sandbox Actions Panel
 *
 * Displays available actions for a running sandbox instance and allows
 * executing them via a dynamic form dialog. Shows execution history.
 */

import React from 'react';
import {
  Text,
  Flex,
  Box,
  Button,
  TagBadge,
  DialogTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  TextField,
  PasswordField,
  Select,
} from '@chatsdet/ui';
import { Play, CheckCircle, XCircle, Clock, Lightning } from '@phosphor-icons/react';
import { CopyablePre } from '../../../ui/components/CopyButton';
import { ApiError } from '../../../lib/api-error';
import { ActionResultView } from './ActionResultView';
import {
  useSandboxActions,
  useSandboxActionExecutions,
  useExecuteSandboxAction,
  type SandboxAction,
  type ActionExecution,
  type ActionExecuteResult,
} from '../hooks/useSandboxActions';

// ---------------------------------------------------------------------------
// Action Execute Dialog
// ---------------------------------------------------------------------------

export function getActionExecutionDiagnostics(
  execution: Pick<ActionExecuteResult, 'error' | 'stderr' | 'stdout'>
): Array<{ label: 'error' | 'stderr' | 'stdout'; value: string }> {
  return [
    { label: 'error' as const, value: execution.error?.trim() ?? '' },
    { label: 'stderr' as const, value: execution.stderr?.trim() ?? '' },
    { label: 'stdout' as const, value: execution.stdout?.trim() ?? '' },
  ].filter((entry) => entry.value.length > 0);
}

function ActionExecuteDialog({
  action,
  instanceId,
  isOpen,
  onOpenChange,
}: {
  action: SandboxAction;
  instanceId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const executeMutation = useExecuteSandboxAction(instanceId);
  const [params, setParams] = React.useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    for (const [key, prop] of Object.entries(action.inputSchema.properties)) {
      if (prop.default !== undefined) defaults[key] = String(prop.default);
    }
    return defaults;
  });

  const handleExecute = () => {
    executeMutation.mutate({ slug: action.slug, params });
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after closing so next open starts fresh
    setTimeout(() => {
      setParams(() => {
        const defaults: Record<string, string> = {};
        for (const [key, prop] of Object.entries(action.inputSchema.properties)) {
          if (prop.default !== undefined) defaults[key] = String(prop.default);
        }
        return defaults;
      });
      executeMutation.reset();
    }, 200);
  };

  const required = new Set(action.inputSchema.required ?? []);

  /** Inline minLength hint — only once the user has typed something. */
  const fieldError = (
    key: string,
    prop: { minLength?: number }
  ): string | undefined => {
    const value = params[key]?.trim() ?? '';
    if (value && prop.minLength && value.length < prop.minLength) {
      return `Must be at least ${prop.minLength} characters`;
    }
    return undefined;
  };

  const isValid = Object.entries(action.inputSchema.properties).every(([key, prop]) => {
    const value = params[key]?.trim() ?? '';
    if (required.has(key) && !value) return false;
    if (value && prop.minLength && value.length < prop.minLength) return false;
    return true;
  });

  // The backend forwards the specific reason as `detail` (e.g. "Parameter
  // password must be at least 16 characters"); the generic catalog message
  // lives on `.message`. Prefer the detail when present.
  const executeErrorDetail =
    executeMutation.error instanceof ApiError &&
    typeof executeMutation.error.details?.detail === 'string'
      ? executeMutation.error.details.detail
      : undefined;
  const executeErrorMessage =
    executeMutation.error instanceof Error ? executeMutation.error.message : undefined;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <span />
      <Dialog
        eyebrow="Run action"
        title={`Execute: ${action.label}`}
        description={action.description ?? undefined}
      >
        <DialogContent>
          <Flex direction="column" gap="md">
            {Object.entries(action.inputSchema.properties).map(([key, prop]) => {
              const value = params[key] ?? '';
              const onChange = (v: string) => setParams({ ...params, [key]: v });

              if (prop.enum) {
                return (
                  <Box key={key}>
                    <Select
                      label={key}
                      isRequired={required.has(key)}
                      placeholder="Select…"
                      items={prop.enum.map((v) => ({ id: v, label: v }))}
                      selectedKey={value || null}
                      onSelectionChange={(k) =>
                        onChange(k === null || k === undefined ? '' : String(k))
                      }
                    />
                    {prop.description && (
                      <Text size="xs" className="text-muted-foreground mt-1">
                        {prop.description}
                      </Text>
                    )}
                  </Box>
                );
              }

              const FieldComponent = prop.sensitive ? PasswordField : TextField;
              return (
                <FieldComponent
                  key={key}
                  label={key}
                  isRequired={required.has(key)}
                  value={value}
                  onChange={onChange}
                  description={prop.description}
                  errorMessage={fieldError(key, prop)}
                  placeholder={prop.sensitive ? '••••••••' : key}
                />
              );
            })}

            {executeMutation.isError && (
              <Box className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <Text size="sm" weight="medium" className="text-destructive">
                  {executeErrorDetail ?? executeErrorMessage ?? 'Failed to execute action'}
                </Text>
                {executeErrorDetail && executeErrorMessage && (
                  <Text size="xs" className="text-destructive/80 mt-0.5">
                    {executeErrorMessage}
                  </Text>
                )}
              </Box>
            )}

            {executeMutation.isSuccess &&
              executeMutation.data &&
              (() => {
                const completed = executeMutation.data.status === 'completed';
                const diagnostics = getActionExecutionDiagnostics(executeMutation.data);

                return (
                  <Box
                    className={
                      completed
                        ? 'p-3 rounded-lg bg-green-500/10 border border-green-500/20'
                        : 'p-3 rounded-lg bg-destructive/10 border border-destructive/20'
                    }
                  >
                    <Text size="sm" className="font-medium mb-1">
                      {completed ? 'Success' : `Failed (exit ${executeMutation.data.exitCode})`}
                    </Text>
                    {executeMutation.data.result && (
                      <ActionResultView result={executeMutation.data.result} />
                    )}
                    {!completed && diagnostics.length > 0 && (
                      <Flex direction="column" gap="xs" className="mt-2">
                        {diagnostics.map((diagnostic) => (
                          <CopyablePre
                            key={diagnostic.label}
                            value={diagnostic.value}
                            label={diagnostic.label}
                          >
                            <pre className="text-xs overflow-auto max-h-32 whitespace-pre-wrap">
                              {diagnostic.value}
                            </pre>
                          </CopyablePre>
                        ))}
                      </Flex>
                    )}
                  </Box>
                );
              })()}
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onPress={handleClose}>
            {executeMutation.isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!executeMutation.isSuccess && (
            <Button
              variant="primary"
              onPress={handleExecute}
              isDisabled={!isValid || executeMutation.isPending}
            >
              {executeMutation.isPending ? 'Executing...' : 'Execute'}
            </Button>
          )}
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}

// ---------------------------------------------------------------------------
// Execution Status Badge
// ---------------------------------------------------------------------------

function ExecutionStatusBadge({ status }: { status: ActionExecution['status'] }) {
  switch (status) {
    case 'completed':
      return <TagBadge name="completed" color="blue" icon={<CheckCircle size={12} />} />;
    case 'failed':
      return <TagBadge name="failed" color="red" icon={<XCircle size={12} />} />;
    case 'running':
      return <TagBadge name="running" color="blue" icon={<Lightning size={12} />} />;
    case 'timeout':
      return <TagBadge name="timeout" color="yellow" icon={<Clock size={12} />} />;
    default:
      return <TagBadge name={status} color="gray" hideDot />;
  }
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export function SandboxActionsPanel({ instanceId }: { instanceId: string }) {
  const actionsQuery = useSandboxActions(instanceId);
  const executionsQuery = useSandboxActionExecutions(instanceId);
  const [selectedAction, setSelectedAction] = React.useState<SandboxAction | null>(null);

  const actions = actionsQuery.data ?? [];
  const executions = executionsQuery.data ?? [];
  const enabledActions = actions.filter((a) => a.enabled);

  if (actionsQuery.isLoading) return null;
  if (enabledActions.length === 0) return null;

  return (
    <>
      {/* Run-panel style, full-bleed: header + hairline rows, each run via a
          small play icon (mirrors the Flow Designer Runs panel). No card box —
          fills the sidebar width. */}
      <div>
        <Flex align="center" gap="sm" className="h-11 px-4 border-b border-border">
          <Lightning size={14} weight="fill" className="text-primary shrink-0" />
          <Text size="sm" weight="semibold" className="flex-1">
            Actions
          </Text>
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {enabledActions.length}
          </Text>
        </Flex>

        <div className="divide-y divide-border">
          {enabledActions.map((action) => (
            <div
              key={action.slug}
              className="group flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/40"
            >
              <div className="min-w-0 flex-1">
                <Text size="sm" className="font-medium truncate">
                  {action.label}
                </Text>
                {action.description && (
                  <Text size="xs" className="text-muted-foreground truncate">
                    {action.description}
                  </Text>
                )}
              </div>
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip={`Run ${action.label}`}
                aria-label={`Run ${action.label}`}
                onPress={() => setSelectedAction(action)}
                iconLeft={<Play size={14} weight="fill" />}
                className="opacity-60 transition-opacity group-hover:opacity-100"
              />
            </div>
          ))}
        </div>

        {executions.length > 0 && (
          <div className="border-t border-border px-4 py-3">
            <Text
              size="xs"
              weight="medium"
              className="uppercase tracking-wide text-muted-foreground mb-2"
            >
              Recent
            </Text>
            <Flex direction="column" gap="xs">
              {executions.slice(0, 5).map((exec) => (
                <Flex key={exec.id} align="center" justify="between" className="text-xs">
                  <Flex align="center" gap="sm">
                    <ExecutionStatusBadge status={exec.status} />
                    <Text size="xs" className="text-muted-foreground">
                      {exec.source}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="sm">
                    {exec.durationMs !== null && (
                      <Text size="xs" className="text-muted-foreground">
                        {exec.durationMs}ms
                      </Text>
                    )}
                    <Text size="xs" className="text-muted-foreground">
                      {new Date(exec.createdAt).toLocaleTimeString()}
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </div>
        )}
      </div>

      {selectedAction && (
        <ActionExecuteDialog
          action={selectedAction}
          instanceId={instanceId}
          isOpen={!!selectedAction}
          onOpenChange={(open) => {
            if (!open) setSelectedAction(null);
          }}
        />
      )}
    </>
  );
}
